import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import type {
  PricingDecisionContextEvidence,
} from '../types/pricingDecision';


interface Props {
  evidence: PricingDecisionContextEvidence;
}


export default function PricingDecisionContextEvidencePanel({
  evidence,
}: Props) {
  if (
    evidence.sections.length === 0
  ) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,

        border:
          '1px solid rgba(56,189,248,0.16)',

        bgcolor:
          'rgba(8,17,31,0.64)',

        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: {
            xs: 1.5,
            md: 1.75,
          },

          py: 1.35,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: '#7dd3fc',
            fontWeight: 850,
            letterSpacing: 0.75,
          }}
        >
          Contexto comercial y evidencia
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 0.25,
            color: '#94a3b8',
            lineHeight: 1.5,
            maxWidth: 900,
          }}
        >
          {evidence.description}
        </Typography>
      </Box>

      <Divider />

      <Box
        sx={{
          p: {
            xs: 1.25,
            md: 1.5,
          },

          display: 'grid',

          gridTemplateColumns: {
            xs: '1fr',

            lg:
              evidence.sections.length >= 3
                ? 'repeat(3, minmax(0, 1fr))'
                : 'repeat(2, minmax(0, 1fr))',
          },

          gap: 1,
        }}
      >
        {evidence.sections.map(
          (section) => (
            <Paper
              key={section.kind}
              elevation={0}
              sx={{
                minWidth: 0,
                p: 1.4,
                borderRadius: 2,

                border:
                  '1px solid rgba(148,163,184,0.13)',

                bgcolor:
                  'rgba(15,23,42,0.28)',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      color: '#64748b',
                      fontWeight: 850,
                      letterSpacing: 0.7,
                      lineHeight: 1.2,
                    }}
                  >
                    {section.eyebrow}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      mt: 0.25,
                      color: '#f8fafc',
                      fontWeight: 850,
                      lineHeight: 1.25,
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>

                {section.statusLabel && (
                  <Chip
                    size="small"
                    label={
                      section.statusLabel
                    }
                    variant="outlined"
                    sx={{
                      flexShrink: 0,
                      height: 22,

                      color: '#cbd5e1',

                      borderColor:
                        'rgba(148,163,184,0.20)',

                      '& .MuiChip-label': {
                        px: 0.8,
                        fontSize: 10.5,
                        fontWeight: 750,
                      },
                    }}
                  />
                )}
              </Stack>

              {section.description && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.65,
                    color: '#64748b',
                    lineHeight: 1.45,
                  }}
                >
                  {section.description}
                </Typography>
              )}

              {section.metrics.length > 0 && (
                <Box
                  sx={{
                    mt: 1.25,

                    display: 'grid',

                    gridTemplateColumns:
                      'repeat(2, minmax(0, 1fr))',

                    gap: 1,
                  }}
                >
                  {section.metrics.map(
                    (metric) => (
                      <Box
                        key={metric.label}
                        sx={{
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#64748b',
                            fontWeight: 700,
                          }}
                        >
                          {metric.label}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.1,
                            color: '#e2e8f0',
                            fontWeight: 850,
                          }}
                        >
                          {metric.value}
                        </Typography>

                        {metric.helper && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.1,
                              color: '#475569',
                            }}
                          >
                            {metric.helper}
                          </Typography>
                        )}
                      </Box>
                    ),
                  )}
                </Box>
              )}

              {section.attributes
                && section.attributes.length > 0 && (
                <>
                  <Divider
                    sx={{
                      my: 1.15,
                    }}
                  />

                  <Stack
                    spacing={0.45}
                  >
                    {section.attributes.map(
                      (attribute) => (
                        <Stack
                          key={attribute.label}
                          direction="row"
                          spacing={1}
                          justifyContent="space-between"
                          alignItems="baseline"
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                            }}
                          >
                            {attribute.label}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#cbd5e1',
                              fontWeight: 750,
                              textAlign: 'right',
                            }}
                          >
                            {attribute.value}
                          </Typography>
                        </Stack>
                      ),
                    )}
                  </Stack>
                </>
              )}
            </Paper>
          ),
        )}
      </Box>
    </Paper>
  );
}